import { DownloadOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Select,
  Statistic,
  Switch,
  Table,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { connect, type DispatchProp } from "react-redux";
import { Card } from "../../common/components/Card.tsx";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import {
  type MaterialUsageReportFilters,
  reportsActions,
} from "../../features/reports/reportsSlice.ts";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { CategoryEnum, MaterialUsageSummaryRow } from "../generated-axios-client";
import {
  getMaterialCategoryLabel,
  getMaterialCategoryOptions,
} from "../materials/materialsConstants.ts";
import type { RootState } from "../store.ts";
import styles from "./MaterialUsageReportPage.module.css";

type FilterFormValues = {
  project?: string;
  location?: string;
  category?: CategoryEnum;
  period?: [Dayjs | null, Dayjs | null] | null;
  includeInProgress: boolean;
};

// Opens on the current month so the page does not read the whole history on every
// visit; clearing the period still shows all time.
const currentMonthFilterValues = (): FilterFormValues => ({
  period: [dayjs().startOf("month"), dayjs().endOf("month")],
  includeInProgress: false,
});

const API_DATE_FORMAT = "YYYY-MM-DD";
// below this the table scrolls sideways instead of squeezing long material names
const MIN_TABLE_WIDTH = 760;

const toReportFilters = (values: FilterFormValues): MaterialUsageReportFilters => ({
  project: values.project,
  location: values.location,
  category: values.category,
  dateFrom: values.period?.[0]?.format(API_DATE_FORMAT),
  dateTo: values.period?.[1]?.format(API_DATE_FORMAT),
  includeInProgress: values.includeInProgress,
});

const byLabel = (options: { label: string }[]) =>
  [...options].sort((first, second) => first.label.localeCompare(second.label));

const getColumns = (intl: IntlShape): ColumnsType<MaterialUsageSummaryRow> => [
  {
    key: "material",
    title: intl.formatMessage({ id: "material_usage_column_material" }),
    width: "45%",
    render: (_, row) => row.material_name ?? "—",
  },
  {
    key: "sap_code",
    title: intl.formatMessage({ id: "material_usage_column_sap_code" }),
    render: (_, row) => row.sap_code ?? "—",
  },
  {
    key: "category",
    title: intl.formatMessage({ id: "material_usage_column_category" }),
    render: (_, row) =>
      row.category ? getMaterialCategoryLabel(intl, row.category) : "—",
  },
  {
    key: "total_amount",
    title: intl.formatMessage({ id: "material_usage_column_total_amount" }),
    align: "right",
    render: (_, row) =>
      `${intl.formatNumber(row.total_amount, { maximumFractionDigits: 3 })} ${
        row.measurement_unit_label ?? ""
      }`.trim(),
  },
  {
    key: "work_order_count",
    title: intl.formatMessage({ id: "material_usage_column_work_orders" }),
    align: "right",
    dataIndex: "work_order_count",
  },
];

type MaterialUsageReportPageProps = ReturnType<typeof withMaterialUsageReport> &
  DispatchProp;

export const MaterialUsageReportPage = (props: MaterialUsageReportPageProps) => {
  const { intl, dispatch, projects, locations, summary, summaryStatus, downloadStatus } =
    props;
  const [form] = Form.useForm<FilterFormValues>();
  const includeInProgress = Form.useWatch("includeInProgress", form);

  const [initialFilterValues] = useState(currentMonthFilterValues);

  useEffect(() => {
    dispatch(
      reportsActions.fetchMaterialUsageSummary(toReportFilters(initialFilterValues))
    );
    return () => {
      dispatch(reportsActions.clearReports());
    };
  }, [dispatch, initialFilterValues]);

  const projectOptions = useMemo(
    () =>
      byLabel(
        (projects ?? []).map((project) => ({
          value: project.id,
          label: project.name ?? "—",
        }))
      ),
    [projects]
  );
  const locationOptions = useMemo(
    () =>
      byLabel(
        (locations ?? []).map((location) => ({
          value: location.id,
          label: location.name ?? "—",
        }))
      ),
    [locations]
  );
  const categoryOptions = useMemo(() => getMaterialCategoryOptions(intl), [intl]);
  const columns = useMemo(() => getColumns(intl), [intl]);

  const summaryLoading = summaryStatus === "loading";
  const hasSummary = summary !== null;

  return (
    <ConnectedAppLayout>
      <PageTitleContainer
        title={intl.formatMessage({ id: "material_usage_report_title" })}
        subtitle={intl.formatMessage({ id: "material_usage_report_subtitle" })}
        extra={
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloadStatus === "loading"}
            disabled={!hasSummary || summaryLoading}
            onClick={() => dispatch(reportsActions.downloadMaterialUsageReport())}
          >
            {intl.formatMessage({ id: "material_usage_download_excel" })}
          </Button>
        }
      >
        <Card>
          <Form
            form={form}
            layout="vertical"
            initialValues={initialFilterValues}
            onValuesChange={(_, allValues) =>
              dispatch(
                reportsActions.fetchMaterialUsageSummary(toReportFilters(allValues))
              )
            }
          >
            <Flex gap="middle" wrap align="end">
              <Form.Item
                name="project"
                label={intl.formatMessage({ id: "material_usage_filter_project" })}
                className={styles.filterItem}
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={projects === undefined}
                  placeholder={intl.formatMessage({ id: "material_usage_all_projects" })}
                  options={projectOptions}
                />
              </Form.Item>
              <Form.Item
                name="location"
                label={intl.formatMessage({ id: "material_usage_filter_site" })}
                className={styles.filterItem}
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={locations === undefined}
                  placeholder={intl.formatMessage({ id: "material_usage_all_sites" })}
                  options={locationOptions}
                />
              </Form.Item>
              <Form.Item
                name="category"
                label={intl.formatMessage({ id: "material_usage_filter_category" })}
                className={styles.filterItem}
              >
                <Select
                  allowClear
                  placeholder={intl.formatMessage({
                    id: "material_usage_all_categories",
                  })}
                  options={categoryOptions}
                />
              </Form.Item>
              <Form.Item
                name="period"
                label={intl.formatMessage({ id: "material_usage_filter_period" })}
                tooltip={intl.formatMessage({ id: "material_usage_filter_period_hint" })}
                className={styles.filterItem}
              >
                <DatePicker.RangePicker allowEmpty={[true, true]} />
              </Form.Item>
              <Form.Item
                name="includeInProgress"
                label={intl.formatMessage({
                  id: "material_usage_filter_include_in_progress",
                })}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Flex>
          </Form>
          <Typography.Text type="secondary">
            {intl.formatMessage({
              id: includeInProgress
                ? "material_usage_statuses_with_in_progress"
                : "material_usage_statuses_default",
            })}
          </Typography.Text>
        </Card>

        <Card>
          <Flex vertical gap="middle">
            <Flex gap="large" wrap>
              <Statistic
                title={intl.formatMessage({ id: "material_usage_stat_work_orders" })}
                value={summary?.work_order_count ?? 0}
                loading={summaryLoading && !hasSummary}
              />
              <Statistic
                title={intl.formatMessage({ id: "material_usage_stat_usages" })}
                value={summary?.usage_count ?? 0}
                loading={summaryLoading && !hasSummary}
              />
              <Statistic
                title={intl.formatMessage({ id: "material_usage_stat_materials" })}
                value={summary?.rows.length ?? 0}
                loading={summaryLoading && !hasSummary}
              />
            </Flex>
            <Table<MaterialUsageSummaryRow>
              rowKey={(row) => `${row.material_id}:${row.measurement_unit ?? ""}`}
              columns={columns}
              dataSource={summary?.rows ?? []}
              loading={summaryLoading}
              pagination={false}
              scroll={{ x: MIN_TABLE_WIDTH }}
              locale={{
                emptyText: intl.formatMessage({
                  id:
                    summaryStatus === "failed"
                      ? "material_usage_load_failed"
                      : "material_usage_empty",
                }),
              }}
            />
          </Flex>
        </Card>
      </PageTitleContainer>
    </ConnectedAppLayout>
  );
};

const withMaterialUsageReport = (state: RootState) => ({
  intl: state.intl.datetimeIntl,
  projects: state.ticketData.projects,
  locations: state.ticketData.locations,
  summary: state.reports.materialUsageSummary,
  summaryStatus: state.reports.materialUsageSummaryStatus,
  downloadStatus: state.reports.materialUsageDownloadStatus,
});

export const ConnectedMaterialUsageReportPage = connect(withMaterialUsageReport)(
  MaterialUsageReportPage
);
export default ConnectedMaterialUsageReportPage;
